import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

const HBAR = 1.054571817e-34; // reduced Planck's constant

export default function HeisenbergUncertaintyCalculator() {
  const [positionUncertainty, setPositionUncertainty] = useState("");
  const [momentumUncertainty, setMomentumUncertainty] = useState("");
  const [result, setResult] = useState<any>(null);

  const calculateFromPosition = () => {
    const dx = parseFloat(positionUncertainty);

    if (isNaN(dx) || dx === 0) {
      toast.error("Please enter valid position uncertainty");
      return;
    }

    const minDp = HBAR / (2 * dx);
    const product = dx * minDp;

    setResult({
      type: "momentum",
      value: minDp,
      product,
    });
    toast.success("Calculation complete!");
  };

  const calculateFromMomentum = () => {
    const dp = parseFloat(momentumUncertainty);

    if (isNaN(dp) || dp === 0) {
      toast.error("Please enter valid momentum uncertainty");
      return;
    }

    const minDx = HBAR / (2 * dp);
    const product = minDx * dp;

    setResult({
      type: "position",
      value: minDx,
      product,
    });
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setPositionUncertainty("");
    setMomentumUncertainty("");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Heisenberg Uncertainty Calculator" calculatorPath="/calculator/heisenberg-uncertainty">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Heisenberg Uncertainty Principle</h1>
          <p className="text-muted-foreground">
            Calculate minimum uncertainty in position or momentum
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: Δx·Δp ≥ ℏ/2</CardTitle>
            <CardDescription>
              Where ℏ = h/(2π) = 1.055×10⁻³⁴ J·s
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="position" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="position">Find Δp from Δx</TabsTrigger>
                <TabsTrigger value="momentum">Find Δx from Δp</TabsTrigger>
              </TabsList>

              <TabsContent value="position" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="positionUncertainty">Position Uncertainty Δx (m)</Label>
                  <Input
                    id="positionUncertainty"
                    type="number"
                    step="any"
                    placeholder="Enter position uncertainty (e.g., 1e-10)"
                    value={positionUncertainty}
                    onChange={(e) => setPositionUncertainty(e.target.value)}
                  />
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateFromPosition} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="momentum" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="momentumUncertainty">Momentum Uncertainty Δp (kg·m/s)</Label>
                  <Input
                    id="momentumUncertainty"
                    type="number"
                    step="any"
                    placeholder="Enter momentum uncertainty (e.g., 1e-24)"
                    value={momentumUncertainty}
                    onChange={(e) => setMomentumUncertainty(e.target.value)}
                  />
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateFromMomentum} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            {result && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Minimum {result.type === "momentum" ? "Momentum" : "Position"} Uncertainty
                      </p>
                      <p className="text-2xl font-bold text-primary">
                        {result.value.toExponential(4)} {result.type === "momentum" ? "kg·m/s" : "m"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Product Δx·Δp</p>
                      <p className="text-lg font-bold text-primary">
                        {result.product.toExponential(4)} J·s
                      </p>
                      <p className="text-xs text-muted-foreground">
                        (Minimum: {(HBAR / 2).toExponential(4)} J·s)
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["heisenberg-uncertainty"]} trigger={result.value} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Cannot know both position and momentum precisely</p>
                <p>• More precise position → less precise momentum</p>
                <p>• Fundamental limit, not measurement error</p>
                <p>• Significant for quantum particles, negligible for large objects</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: Δx = 1 Å → Δp ≥ 5.3×10⁻²⁵ kg·m/s
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
